import dynamic from "next/dynamic";
import {
  Header,
  ProductHub,
  BuilderFrameIconButton,
  SideBar,
  BuilderFrameButton,
  ProductListButton,
  HeaderLayout,
  BuilderFrameLayout,
  BaseDrawer,
  SideBarCopilotItem,
} from "@zapier/product-nav";
import {
  SUMMARY,
  SideBarSelected,
  isPublicAtom,
  isViewOnlyModeAtom,
  sessionAtom,
  sideBarSelectedAtom,
  hasZslAtom,
  showZslFeedbackAtom,
  canvasAtom,
  isDraftModeIncludingTemplatesAtom,
  closeSidebarSetter,
  shouldCopilotBeVisibleAtom,
  isCopilotEnabledAtom_SUSPENDS,
} from "@zapier/canvas-state";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { Suspense, useCallback, useState, useEffect } from "react";
import { COPILOT_DISABLED_TOOLTIP } from "@zapier/ai-toolkit/client";
import { Spacer } from "@zapier/design-system";

const InteractiveName = dynamic(
  () =>
    import("../InteractiveName").then((mod) => ({
      default: mod.InteractiveName,
    })),
  {
    ssr: false,
    loading: () => null,
  },
);

const Banner = dynamic(
  () =>
    import("./Banner").then((mod) => ({
      default: mod.Banner,
    })),
  {
    ssr: false,
    loading: () => null,
  },
);
import ShareButton, { LoadingShareButton } from "../ShareButton/ShareButton";
import { ZoomSelect } from "../ZoomSelect";
import { HelpMenu } from "../HelpMenu";
import { SidebarDrawer } from "../SidebarDrawer";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";
import {
  useCopilotDrawerState,
  useIsCopilotNoPilotDisabled,
} from "@zapier/canvas-state";
import { DiagrammingToolbar } from "../DiagrammingToolbar";
import { TemplateFeedback } from "../TemplateFeedback";
import styles from "./BuilderNav.module.css";

type Props = {
  children: React.ReactNode;
};

// TODO: Import this from @zapier/product-nav when possible
type HeaderItemProps = {
  component: React.ReactElement | null;
  hideOnMobile?: boolean;
  hideOnTablet?: boolean;
  showOnTablet?: boolean;
  showOnMobile?: boolean;
};

const NO_PILOT_DISABLED_TOOLTIP =
  "Copilot is disabled for this account. Request access through Zapier support.";

export function BuilderNav({ children }: Props) {
  const canvas = useAtomValue(canvasAtom);
  const hasZsl = useAtomValue(hasZslAtom);
  const sideBarSelected = useAtomValue(sideBarSelectedAtom);
  const session = useAtomValue(sessionAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isPublic = useAtomValue(isPublicAtom);
  const [showTemplateFeedback, setShowTemplateFeedback] =
    useAtom(showZslFeedbackAtom);
  const isDraftModeIncludingTemplates = useAtomValue(
    isDraftModeIncludingTemplatesAtom,
  );

  const [isCopilotOpen, setIsCopilotOpen] = useCopilotDrawerState();
  const shouldCopilotBeVisible = useAtomValue(shouldCopilotBeVisibleAtom);

  const [isClientMounted, setIsClientMounted] = useState(false);

  /*
   * ⚠️ HYDRATION FIX: Prevents server/client mismatch with localStorage-dependent state from useCopilotDrawerState from ai-toolkit.
   * Server renders isOpen={true}, client renders correct state after mount.
   * DO NOT REMOVE - prevents "server rendered HTML didn't match client properties" error.
   * You'll see this locally https://cdn.zappy.app/37824c2a6a4a3f3d3af571fbcc370e0f.png
   */
  useEffect(() => {
    setIsClientMounted(true);
  }, []);

  const closeSidebar = useSetAtom(closeSidebarSetter);

  const handleCloseSidebarOrCopilot = useCallback(() => {
    if (isCopilotOpen) {
      setIsCopilotOpen(false);
    } else {
      closeSidebar();
    }
  }, [closeSidebar, isCopilotOpen, setIsCopilotOpen]);

  const handleSideBarItemPress = useAtomCallback(
    useCallback(
      (get, set, selectedItem: SideBarSelected) => {
        const sideBarSelected = get(sideBarSelectedAtom);
        if (sideBarSelected === selectedItem) {
          closeSidebar();
        } else {
          // Always close copilot when we click to open other sidebar items or else the sidebar won't show up
          setIsCopilotOpen(false);
          set(sideBarSelectedAtom, selectedItem);
        }
      },
      [closeSidebar, setIsCopilotOpen],
    ),
  );

  const rightColumnComponents: HeaderItemProps[] = [
    !hasZsl &&
      !isPublic &&
      isClientMounted && {
        component: (
          <Suspense fallback={<LoadingShareButton />}>
            <ShareButton />
            <Spacer width={5} />
          </Suspense>
        ),
        hideOnTablet: true,
      },
    session?.is_logged_in &&
      session?.current_account_id === canvas.account_id &&
      isPublic && {
        component: (
          <BuilderFrameButton href={`/app/canvas/${canvas.id}`}>
            Edit
          </BuilderFrameButton>
        ),
      },
    !session?.is_logged_in &&
      isPublic && {
        component: (
          <BuilderFrameButton href={`/app/login?next=/app/canvas/${canvas.id}`}>
            Log in to edit
          </BuilderFrameButton>
        ),
        hideOnTablet: true,
      },
    {
      component: <ZoomSelect />,
      hideOnTablet: true,
    },
    {
      component: <HelpMenu />,
      hideOnTablet: false,
    },
  ].filter(Boolean) as HeaderItemProps[];

  const { SideBarItem } = SideBar;

  return (
    <>
      <BuilderFrameLayout
        header={
          <div>
            <Header isResponsive={true}>
              <HeaderLayout
                leftColumnComponents={[
                  {
                    component: (
                      <BuilderFrameIconButton
                        icon="navHome"
                        label="Home"
                        href="/"
                        aria-label="Home"
                      />
                    ),
                  },
                  { component: <Spacer width={15} />, hideOnMobile: true },
                  {
                    component: (
                      <ProductHub
                        productName="Canvas"
                        // TODO: Add this footer in later when we get copy requirements
                        // footer={
                        //   !account?.plan_info.allow_companies ? (
                        //     <>
                        //       You are on the{" "}
                        //       <strong>{account?.plan_info.name}</strong> plan.
                        //       Automate at scale with our <a href="/">Company</a>{" "}
                        //       plan
                        //     </>
                        //   ) : null
                        // }
                      />
                    ),
                    hideOnMobile: true,
                  },
                  { component: <Spacer width={4} />, hideOnTablet: true },
                  {
                    component: (
                      <ProductListButton
                        productName="Canvas"
                        productListHref="/app/assets/canvases"
                      />
                    ),
                    hideOnTablet: true,
                  },
                  { component: <Spacer width={10} />, hideOnTablet: true },
                  // TODO: Add this in later
                  // {
                  //   component: (
                  //     <UpgradeCTA
                  //       planName={account?.plan_info.name || "Free"}
                  //       productName="Canvas"
                  //       upgradeHref="https://zapier.com/free-trial/"
                  //     />
                  //   ),
                  //   hideOnTablet: true,
                  // },
                ]}
                centerColumnComponents={[
                  {
                    component: (
                      <InteractiveName
                        canvasName={canvas.name}
                        ownerId={canvas.customuser_id!}
                        permissions={isViewOnlyMode ? "view" : "edit"}
                      />
                    ),
                  },
                ]}
                rightColumnComponents={rightColumnComponents}
              />
            </Header>
            <Banner />
          </div>
        }
        sidebar={
          <div style={{ display: "flex" }}>
            <SideBar
              isResponsive={true}
              bottomElement={
                isClientMounted && shouldCopilotBeVisible ? (
                  <Suspense fallback={null}>
                    <SuspendedSideBarCopilotItem />
                  </Suspense>
                ) : null
              }
            >
              <SideBarItem
                name={SUMMARY}
                icon="fileGuide"
                tooltipTitle={capitalizeFirstLetter(SUMMARY)}
                selected={sideBarSelected === SUMMARY}
                onPress={() => handleSideBarItemPress(SUMMARY)}
                isResponsive={true}
              />
              {(session?.is_staff || session?.is_masquerade) && (
                <SideBarItem
                  name="devtools"
                  icon="actionBug"
                  tooltipTitle="DevTools"
                  selected={sideBarSelected === "devTools"}
                  onPress={() => handleSideBarItemPress("devTools")}
                  isResponsive={true}
                />
              )}
            </SideBar>
          </div>
        }
        mainContent={
          <div style={{ display: "flex", height: "100%", width: "100%" }}>
            <BaseDrawer
              onClose={handleCloseSidebarOrCopilot}
              isOpen={
                isClientMounted && Boolean(sideBarSelected || isCopilotOpen)
              }
              className={styles.drawerOverrides}
              isFixed={true}
              isResponsive={true}
              closeOnOutsideClick={false}
            >
              <SidebarDrawer onCloseClick={handleCloseSidebarOrCopilot} />
            </BaseDrawer>
            {children}
            {!isViewOnlyMode && !isDraftModeIncludingTemplates && (
              <Suspense fallback={null}>
                <DiagrammingToolbar />
              </Suspense>
            )}
          </div>
        }
      />
      {showTemplateFeedback && (
        <TemplateFeedback onClose={() => setShowTemplateFeedback(false)} />
      )}
    </>
  );
}

function SuspendedSideBarCopilotItem() {
  const isCopilotEnabled = useAtomValue(isCopilotEnabledAtom_SUSPENDS);
  const closeSidebar = useSetAtom(closeSidebarSetter);
  const [isCopilotOpen, setIsCopilotOpen] = useCopilotDrawerState();
  const isCopilotNoPilotDisabled = useIsCopilotNoPilotDisabled();

  const toggleCopilot = useCallback(() => {
    closeSidebar();
    setIsCopilotOpen((prevState) => !prevState);
  }, [closeSidebar, setIsCopilotOpen]);

  const isCopilotDisabled = !isCopilotEnabled || isCopilotNoPilotDisabled;

  const copilotTooltipTitle = isCopilotDisabled
    ? isCopilotNoPilotDisabled
      ? NO_PILOT_DISABLED_TOOLTIP
      : COPILOT_DISABLED_TOOLTIP
    : "Copilot";

  return (
    <SideBarCopilotItem
      onPress={toggleCopilot}
      selected={isCopilotOpen}
      isLocked={isCopilotDisabled}
      tooltipTitle={copilotTooltipTitle}
    />
  );
}
